import type { LoaderFunction, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import Alert from "~/ui/Alert";
import Card from "~/ui/Card";
import Heading from "~/ui/Heading";
import LinkButton from "~/ui/LinkButton";
import SpacedContent from "~/ui/SpacedContent";
import hasBookingTerms from "~/company/helpers/hasBookingTerms";
import getFixedTranslation from "~/kernel/internationalization/getFixedTranslation.server";
import translationHandle from "~/kernel/internationalization/translationHandle";
import useTranslation from "~/kernel/internationalization/useTranslation";
import title from "~/kernel/meta/title";
import ROUTES from "~/kernel/routing/ROUTES";
import requireResource from "~/kernel/server/requireResource";
import companyRepository from "~/repositories/companyRepository";

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: title(data.pageTitle) },
];

export const handle = translationHandle({
  ns: { bookingTerms: true },
});

export const loader: LoaderFunction = async ({ request, params }) => {
  const translation = await getFixedTranslation(request, handle);
  const slug = params.slug;
  requireResource(slug);
  const company = await companyRepository.findBySlug(slug);
  requireResource(company);
  const companyName = company.name;
  const pageTitle = translation.t("show.pageTitle", { companyName });
  return json({ company, pageTitle });
};

export default function BookingTermsPage() {
  const { company } = useLoaderData<typeof loader>();
  const { t } = useTranslation();

  if (hasBookingTerms(company)) {
    return (
      <Card>
        <SpacedContent>
          <Heading>
            {t("show.pageTitle", { companyName: company.name })}
          </Heading>
          <p className="whitespace-pre-line">{company.bookingTerms}</p>
          <LinkButton
            to={ROUTES.company.path({ params: { slug: company.slug } })}
            fullWidth={false}
            variant="secondary"
          >
            {t("back")}
          </LinkButton>
        </SpacedContent>
      </Card>
    );
  }

  return (
    <Card>
      <SpacedContent>
        <Alert type="info">
          {t("show.noBookingTerms", { companyName: company.name })}
        </Alert>
        <LinkButton
          to={ROUTES.company.path({ params: { slug: company.slug } })}
          fullWidth={false}
          variant="secondary"
        >
          {t("back")}
        </LinkButton>
      </SpacedContent>
    </Card>
  );
}
